import {computed, reactive, ref} from 'vue';
import * as KlipApi from '@/api/klip-api.proxy';
import {handleAbortError} from '@/app/shared/helpers/kl-abort-controller';
import {IpproDatatable} from "@/app/shared/components/ippro-datatable/ippro-datatable-declare";
import {concat, first, isEmpty, isEqual} from 'lodash-es';
import QueryFilter from '@/app/shared/helpers/queryFilter';
import DateUtil from '@/app/shared/helpers/date-util';
import {
    GroupedCodeListValueOrder,
    ICodeListValueOrder,
    MapRequestToConfirmListItem,
    SearchOption
} from '@/api/klip-api.proxy';
import {RouteLocation, Router} from 'vue-router';
import userLocalStorage from '@/storage/user-local-storage';
import * as UnaModel from './shared/una-model';
import {IMapRequestListItem, IMapRequestsReplied, IMapRequestsToConfirm, IMapRequestsToReply} from './shared/una-model';

export interface IMapRequestMeta {
    totalRows: number;
    resultsPerPage: number;
    currentPage: number | undefined;
}

export interface IFilterData<
    API_GETMAPREQUESTS_INPUT extends KlipApi.IGetMapRequestsToConfirmInput | KlipApi.IGetMapRequestsToReplyInput | KlipApi.IGetMapRequestsRepliedInput
> {
    searchFields: KlipApi.ICodeListValueOrder[];
    searchOptions: KlipApi.ICodeListValueOrder[];
    defaultSearchFilter: API_GETMAPREQUESTS_INPUT;
    groupedSearchOptions: GroupedCodeListValueOrder[];
    zones: ICodeListValueOrder[];
}

export function create_MapRequestContext<
    API_GETMAPREQUESTS_INPUT extends KlipApi.IGetMapRequestsToConfirmInput | KlipApi.IGetMapRequestsToReplyInput | KlipApi.IGetMapRequestsRepliedInput
>
(
    router: Router,
    route: RouteLocation<string>,
    queryTypeDefinition: Object,
    searchOptionStorageKey: string,
    fetchFie: (input: API_GETMAPREQUESTS_INPUT) => Promise<{ count: number, items: UnaModel.IMapRequestListItem[] }>,
    cleanupInputFie: (input: API_GETMAPREQUESTS_INPUT) => API_GETMAPREQUESTS_INPUT,
) {

    const _queryFilter = new QueryFilter(router);

    const mapRequests = ref<UnaModel.IMapRequestListItem[]>([]);

    const mapRequestsColumns = ref<KlipApi.IColumn[]>([]);
    const mapRequestsMeta = ref<IMapRequestMeta>({
        totalRows: 0,
        resultsPerPage: 50,
        currentPage: 1
    });
    const mapRequestsFetching = ref<boolean>(false);
    const mapRequestsInput = ref<API_GETMAPREQUESTS_INPUT>(null);
    const filterData = ref<IFilterData<API_GETMAPREQUESTS_INPUT>>({
        searchFields: [],
        searchOptions: [],
        defaultSearchFilter: null,
        groupedSearchOptions: [],
        zones: null,
    });


    const _lastMapRequestInput = ref<API_GETMAPREQUESTS_INPUT>(null);

    const hasMapRequests = computed(() => mapRequests.value.length > 0);
    const currentPage = computed(() => mapRequestsMeta.value.currentPage);
    const totalPages = computed(() => Math.ceil(mapRequestsMeta.value.totalRows / mapRequestsMeta.value.resultsPerPage));

    const _viewData = ref<UnaModel.IMapRequests<API_GETMAPREQUESTS_INPUT>>(null);



    async function init(result: UnaModel.IMapRequests<API_GETMAPREQUESTS_INPUT>, cellParser?: (row: IpproDatatable.IRow, column: IpproDatatable.IColumn) => any) {
        _viewData.value = result;

        _initializeFilterData(result);
        _initializeMapRequestsInput(result);
        _initializePagination();
        _initializeColumns(cellParser);

        await _refreshMapRequests(false);
    }

    function _initializeFilterData(result: UnaModel.IMapRequests<API_GETMAPREQUESTS_INPUT>) {
        filterData.value = {
            searchFields: result.searchFields,
            searchOptions: result.searchOptions,
            defaultSearchFilter: result.defaultMapRequestsInput,
            groupedSearchOptions: result.groupedSearchOptions,
            zones: result.zones,
        };

        if (!filterData.value.defaultSearchFilter.searchOption) {
            filterData.value.defaultSearchFilter.searchOption = SearchOption.All; // first(filterData.value.searchOptions)?.key;
        }
    }

    function setColumns(columns: KlipApi.IColumn[], cellParser?: (row: IpproDatatable.IRow, column: IpproDatatable.IColumn) => any) {
        if (cellParser) {
            mapRequestsColumns.value = columns.map(column => ({
                ...column,
                parser: cellParser
            }));
        } else {
            mapRequestsColumns.value = columns;
        }
    }

    async function updatePagination(page: number) {
        if (!mapRequestsInput.value) {
            return;
        }
        mapRequestsInput.value.offset = mapRequestsMeta.value.resultsPerPage * (page - 1);
        mapRequestsMeta.value.currentPage = page;

        await _refreshMapRequests(false);
    }

    async function updateSorting(column: KlipApi.IColumn) {
        if (!mapRequestsInput.value) {
            return;
        }
        mapRequestsInput.value.offset = 0;
        mapRequestsMeta.value.currentPage = 1;
        //mapRequestsInput.value.orderField = column.key?.toLowerCase();
        // TODO... iets begrijpbaars van maken?
        mapRequestsInput.value.orderField = KlipApi.MapRequestsToConfirmOrderField[column.key[0].toUpperCase() + column.key.substr(1) as keyof typeof KlipApi.MapRequestsToConfirmOrderField];
        mapRequestsInput.value.orderDirection = column.direction;

        await _refreshMapRequests(false);
    }

    function _calculateCurrentPageFromOffset(offset: number) {
        if (offset && offset % mapRequestsMeta.value.resultsPerPage === 0) {
            mapRequestsMeta.value.currentPage = Math.ceil(offset / mapRequestsMeta.value.resultsPerPage) + 1;
        } else {
            mapRequestsMeta.value.currentPage = 1;
        }
    }

    function _initializeMapRequestsInput(result: UnaModel.IMapRequests<API_GETMAPREQUESTS_INPUT>) {
        mapRequestsInput.value = _queryFilter.mapFilterFromQuery(
            route,
            result.defaultMapRequestsInput,
            result.getMapRequestsInput,
            queryTypeDefinition,
        );
    }

    function _initializePagination() {
        _calculateCurrentPageFromOffset(+route.query.offset);
    }

    function _initializeColumns(cellParser?: (row: IpproDatatable.IRow, column: IpproDatatable.IColumn) => any) {
        console.assert(_viewData.value, 'init first');
        _queryFilter.mapColumnsFromQuery(route, _viewData.value.mapRequestColumns, KlipApi.OrderField);
        setColumns(_viewData.value.mapRequestColumns, cellParser);
    }

    async function _refreshMapRequests(forceRefresh: boolean) {
        if (!mapRequestsInput.value) {
            return;
        }
        const cleanInput = cleanupInputFie(mapRequestsInput.value);
        if (!forceRefresh && isEqual(cleanInput, _lastMapRequestInput.value)) {
            // Let op, dit kan de bedoeling zijn als we een refresh verwachten van de laatste search!!
            // bv na signalr event
            //console.log('TODO: _refreshMapRequests blocked > identical query')
            return;
        }

        mapRequestsFetching.value = true;
        try {
            const { count, items } = await fetchFie(mapRequestsInput.value);

            mapRequestsMeta.value.totalRows = count;
            mapRequests.value = items;

            _lastMapRequestInput.value = cleanInput;
            _queryFilter.mapQuery(route, mapRequestsInput.value);
        } finally {
            mapRequestsFetching.value = false;
        }
    }

    async function refresh() {
        await _refreshMapRequests(true);
    }

    async function updateFilter(input: API_GETMAPREQUESTS_INPUT) {

        const mergedInput = {
            ...mapRequestsInput.value,
            ...input,
        };

        const refresh = !isEqual(input, mapRequestsInput.value);
        if (refresh) {
            mapRequestsInput.value = mergedInput;

            mapRequestsInput.value.offset = 0;
            mapRequestsMeta.value.currentPage = 1;

            await _refreshMapRequests(false);
        }

        _setSearchOptionToLocalStorage(input, searchOptionStorageKey);
    }

    // TEMP: hoort alleen gebruikt te worden vanuit de una-store
    // TODO: refactor
    function __removeMapRequests(idPairs: { mapRequestId: string, zoneId: string}[]): UnaModel.IMapRequestListItem[] {
        if (isEmpty(idPairs)) {
            return [];
        }

        const removedMapRequests: UnaModel.IMapRequestListItem[] = [];
        const remainingMapRequests: UnaModel.IMapRequestListItem[] = [];

        mapRequests.value.forEach((mapRequest) => {
            const toRemove = idPairs.find((x) => mapRequest.mapRequestId === x.mapRequestId && mapRequest.unaZoneId === x.zoneId);
            if (toRemove) {
                removedMapRequests.push(mapRequest);
            } else {
                remainingMapRequests.push(mapRequest);
            }
        });

        mapRequests.value = remainingMapRequests;
        mapRequestsMeta.value.totalRows -= removedMapRequests.length;
        mapRequestsMeta.value.currentPage = 0; // altijd een goed idee om terug naar pagina 1 te gaan?

        return removedMapRequests;
    }

    // TEMP: hoort alleen gebruikt te worden vanuit de una-store
    // TODO: refactor
    function __addMapRequests(newMapRequests: UnaModel.IMapRequestListItem[]) {
        // We assume that the newMapRequests are not already in the current mapRequests.
        // TODO: verify with assert

        mapRequests.value = concat(mapRequests.value, newMapRequests);
        mapRequestsMeta.value.totalRows += newMapRequests.length;
    }

    function _setSearchOptionToLocalStorage(searchField: API_GETMAPREQUESTS_INPUT, searchOptionLocalStorageKey: string) {
        const isSubOrgSelected = isNaN(+searchField.searchOption);
        userLocalStorage.setItem(searchOptionLocalStorageKey, isSubOrgSelected ? searchField.organisationId : searchField.searchOption);
    }


    function reset() {
        mapRequests.value = [];
        mapRequestsColumns.value = [];
        mapRequestsMeta.value = {
            totalRows: 0,
            resultsPerPage: 50,
            currentPage: 1
        };
        mapRequestsFetching.value = false;
        mapRequestsInput.value = undefined;
        _lastMapRequestInput.value = null;
    }

    return {
        mapRequests,
        mapRequestsColumns,
        mapRequestsMeta,
        mapRequestsFetching,
        mapRequestsInput,
        filterData,
        hasMapRequests,
        currentPage,
        totalPages,

        init,
        reset,
        updatePagination,
        updateSorting,
        updateFilter,
        refresh,

        // store only
        __removeMapRequests,
        __addMapRequests,
    }
}
